import { useTranslation } from "react-i18next";

import { CLOCK_CADENCE, useWallClock } from "@/providers/wall-clock";
import { formatPreciseTime } from "verdi-common/utils/timeUtils";

/**
 * Hook that returns an auto-updating, translated precise time string.
 * Subscribes at MINUTES_1 cadence — the display transitions to an absolute date after 1 hour,
 * so sub-minute updates would cause unnecessary re-renders with no visible change.
 */
export function usePreciseTime(timestampMs: number): {
    /** Raw formatted time label, without any "ago" suffix. */
    timeString: string;
    isDateString: boolean;
    /** Ready-to-display label; appends the i18n "ago" suffix for relative strings, unchanged for absolute dates. */
    timeStringWithAgoSuffix: string;
} {
    const { t } = useTranslation(["common"]);
    const nowMs = useWallClock(CLOCK_CADENCE.MINUTES_1);

    const monthKey = new Date(timestampMs).getMonth();
    const { label: timeString, isDate: isDateString } = formatPreciseTime(timestampMs, {
        minLabel: t("time.minute.short"),
        monthLabel: t(`months.short.${monthKey}`),
        nowMs,
    });

    const timeStringWithAgoSuffix = isDateString ? timeString : t("time.time-ago", { timeAgo: timeString });

    return { timeString, isDateString, timeStringWithAgoSuffix };
}
